import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
    changeOwnPassword,
    fetchMe,
    isSignedIn,
    PASSWORD_CHANGE_EVENT,
    SESSION_EXPIRED_EVENT,
    resumeSession,
    setPreferredLocale,
    signIn as apiSignIn,
    signOut as apiSignOut,
    verifyTwoFactor,
} from '../api/client';
import type { TwoFactorMethod, TwoFactorStep } from '../api/client';
import type { Me } from '../api/types';
import { publicLanguages } from '../config';
import { stopRealtime } from '../realtime';
import { directionOf, translator } from '../i18n';
import type { Translate } from '../i18n';

export type Session =
    | { readonly status: 'loading' }
    | { readonly status: 'anonymous' }
    | { readonly status: 'active'; readonly me: Me };

/**
 * Un message à montrer sur l'écran de connexion, après un événement qui y a
 * reconduit : on ne renvoie pas quelqu'un au formulaire sans lui dire pourquoi.
 */
export type Notice = 'passwordChanged' | 'passwordSet' | 'sessionExpired';

export interface SessionContextValue {
    readonly session: Session;
    readonly t: Translate;
    /** La langue dans laquelle `t` traduit — aussi celle des dates. */
    readonly locale: string;
    readonly notice: Notice | null;
    /** Null : la session est ouverte. Sinon le second facteur à demander (D39). */
    signIn(email: string, password: string): Promise<TwoFactorStep | null>;
    /** D39 : le code du second facteur ; lève une ApiProblem si refusé. */
    completeTwoFactor(challenge: string, method: TwoFactorMethod, code: string): Promise<void>;
    signOut(): Promise<void>;
    chooseLocale(code: string): Promise<void>;
    /**
     * D30 : toutes les sessions sont coupées, celle-ci comprise. La session
     * passe à « anonyme » avec un avis ; les erreurs de validation remontent à
     * l'appelant (ApiProblem) pour être affichées sous leur champ.
     */
    changePassword(currentPassword: string, newPassword: string): Promise<void>;
    announce(notice: Notice | null): void;
    /** Relit /api/me : après un changement qui s'y voit (sa propre photo, son nom). */
    refreshMe(): Promise<void>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

const LOCALE_STORAGE_KEY = 'kelassi.locale';

/**
 * La langue choisie survit au rechargement, PAR NAVIGATEUR et non par compte :
 * `user_account` n'a pas encore de colonne de langue, et en ajouter une est une
 * migration. Le choix voyage donc en `Accept-Language`, que le serveur sait déjà
 * résoudre contre les langues activées par l'établissement. Le jour où la
 * colonne existe, seul cet endroit change.
 *
 * Les accès sont gardés : `localStorage` lève en navigation privée sur certains
 * navigateurs, et une préférence d'affichage ne doit jamais empêcher l'ouverture
 * de l'application.
 */
function rememberedLocale(): string | null {
    try {
        return window.localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
        return null;
    }
}

function remember(code: string): void {
    try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
    } catch {
        // Sans effet : la langue vaudra celle du navigateur au prochain chargement.
    }
}

/**
 * La langue de l'interface AVANT connexion : celle choisie dans ce navigateur,
 * sinon celle du navigateur s'il demande l'anglais, sinon le français. Une fois
 * connecté, c'est `/api/me` qui décide (D21).
 */
function anonymousLocale(): string {
    const offered = publicLanguages();
    const remembered = rememberedLocale();

    if (remembered !== null && offered.includes(remembered)) {
        return remembered;
    }

    const browser = navigator.language.toLowerCase().slice(0, 2);

    return offered.includes(browser) ? browser : (offered[0] ?? 'fr');
}

export function SessionProvider({ children }: { readonly children: ReactNode }): ReactNode {
    const [session, setSession] = useState<Session>({ status: 'loading' });
    const [notice, setNotice] = useState<Notice | null>(null);
    // Avant connexion, la langue choisie doit redessiner les pages publiques.
    const [publicLocale, setPublicLocale] = useState<string>(anonymousLocale);

    const load = useCallback(async () => {
        setSession({ status: 'active', me: await fetchMe() });
    }, []);

    useEffect(() => {
        setPreferredLocale(rememberedLocale());

        let abandoned = false;

        // StrictMode joue cet effet DEUX FOIS en développement. C'est sans
        // danger ici uniquement parce que resumeSession() partage une promesse
        // unique : deux rafraîchissements concurrents présenteraient le même
        // cookie, le second serait vu comme un rejeu, et toute la famille de
        // jetons serait révoquée. StrictMode est donc le meilleur détecteur de
        // cette faute — encore faut-il ne pas l'avoir commise.
        void (async () => {
            const resumed = await resumeSession();

            if (abandoned) {
                return;
            }

            if (!resumed) {
                setSession({ status: 'anonymous' });

                return;
            }

            try {
                setSession({ status: 'active', me: await fetchMe() });
            } catch {
                setSession({ status: 'anonymous' });
            }
        })();

        return () => {
            abandoned = true;
        };
    }, []);

    /**
     * D32 — un administrateur a fixé un mot de passe provisoire pendant que ce
     * jeton d'accès vivait encore : le serveur refuse désormais tout. Relire
     * /api/me suffit, il porte `mustChangePassword` et l'application bascule
     * d'elle-même sur le formulaire de changement.
     */
    useEffect(() => {
        const reload = (): void => {
            void load().catch(() => setSession({ status: 'anonymous' }));
        };

        window.addEventListener(PASSWORD_CHANGE_EVENT, reload);

        return () => window.removeEventListener(PASSWORD_CHANGE_EVENT, reload);
    }, [load]);

    /** D41 — la session est morte en cours d'usage : retour à la connexion, avec un avis. */
    useEffect(() => {
        const expired = (): void => {
            stopRealtime();
            setNotice('sessionExpired');
            setSession({ status: 'anonymous' });
        };

        window.addEventListener(SESSION_EXPIRED_EVENT, expired);

        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
    }, []);

    /**
     * La langue et le sens d'écriture posés sur <html>, à la source.
     *
     * `dir` gouverne toute la mise en page : avec des propriétés logiques
     * (`margin-inline-start` plutôt que `margin-left`), l'interface se retourne
     * sans qu'une seule règle CSS soit dupliquée. C'est ce que D21 appelle
     * « le RTL dès le premier écran » — non pas traduire en arabe, mais ne
     * jamais écrire une règle qui empêcherait de le faire.
     */
    useEffect(() => {
        if (session.status === 'active') {
            document.documentElement.lang = session.me.locale.code;
            document.documentElement.dir = session.me.locale.direction;

            return;
        }

        // D43 : les pages publiques (connexion, mot de passe oublié) se
        // retournent aussi en arabe, avant que /api/me ait parlé.
        document.documentElement.lang = publicLocale;
        document.documentElement.dir = directionOf(publicLocale);
    }, [session, publicLocale]);

    const value = useMemo<SessionContextValue>(() => {
        const locale = session.status === 'active' ? session.me.locale.code : publicLocale;

        return {
            session,
            t: translator(locale),
            locale,
            notice,
            async signIn(email, password) {
                const step = await apiSignIn(email, password);

                if (step !== null) {
                    return step;
                }

                setNotice(null);
                await load();

                return null;
            },
            async completeTwoFactor(challenge, method, code) {
                await verifyTwoFactor(challenge, method, code);
                setNotice(null);
                await load();
            },
            async signOut() {
                // D67 : le flux temps réel se ferme avant que le jeton ne soit révoqué.
                stopRealtime();
                await apiSignOut();
                setSession({ status: 'anonymous' });
            },
            async changePassword(currentPassword, newPassword) {
                await changeOwnPassword(currentPassword, newPassword);
                setNotice('passwordChanged');
                setSession({ status: 'anonymous' });
            },
            announce(next) {
                setNotice(next);
            },
            async refreshMe() {
                await load();
            },
            async chooseLocale(code) {
                remember(code);
                setPreferredLocale(code);
                setPublicLocale(code);

                if (isSignedIn()) {
                    await load();
                }
            },
        };
    }, [session, notice, load, publicLocale]);

    return <SessionContext value={value}>{children}</SessionContext>;
}
